/**
 * Who is in, in what proportion, and what that comes to per person.
 *
 * The editor draws `SplitDraft` and hands changes back; everything that
 * decides anything — what each mode's inputs mean, what they resolve to, what
 * is wrong with them — is `src/lib/split-draft.ts`, which is a pure module and
 * is where the tests are.
 *
 * **The odd cent is on the person who takes it.** Every row shows the exact
 * cents that person will owe, straight out of `resolveSplit`, so $81.31 four
 * ways reads 20.33 / 20.33 / 20.33 / 20.32 and the 20.32 is visibly somebody's
 * rather than rounded away into a number that does not add up. It is also the
 * same person every time it is recalculated: the participants are in the
 * group's member order, which is what breaks the tie, and that order does not
 * depend on the order the boxes were ticked.
 *
 * The footer states the reconciliation in words either way — "adds up to
 * $81.31" or the sentence saying what does not — because "does this add up?"
 * is the only question this control exists to answer and a person should not
 * have to add four numbers in their head to find out.
 */

import type { Member } from '../api'
import { formatMoney } from '../lib/format'
import { SPLIT_MODES, type SplitMode } from '../lib/money'
import {
  MODE_HINTS,
  MODE_LABELS,
  previewSplit,
  toggleParticipant,
  withMode,
  withTyped,
  type SplitDraft,
} from '../lib/split-draft'

/** Which map a mode types into, and how its box is labelled and prefixed. */
const FIELDS = {
  exact: { key: 'amounts', suffix: null, prefix: null, hint: 'amount' },
  percentage: { key: 'percents', suffix: '%', prefix: null, hint: 'percentage' },
  shares: { key: 'shares', suffix: null, prefix: '×', hint: 'shares' },
  adjustment: { key: 'adjustments', suffix: null, prefix: '±', hint: 'adjustment' },
} as const

export function SplitEditor({
  split,
  amountCents,
  currency,
  myId,
  members,
  onChange,
}: {
  split: SplitDraft
  amountCents: number
  currency: string
  myId: string
  /** In the group's own order — the order an odd cent is broken by. */
  members: readonly Member[]
  onChange: (next: SplitDraft) => void
}) {
  const memberOrder = members.map((member) => member.userId)
  const preview = previewSplit(amountCents, split, memberOrder)
  const owed = new Map(preview.allocations.map((entry) => [entry.userId, entry.amountCents]))
  const field = split.mode === 'equal' ? null : FIELDS[split.mode]

  return (
    <fieldset className="form__row split-editor">
      <legend>Split</legend>

      {/*
        Toggle buttons rather than `role="tab"`: there are no tab panels here,
        only one list that changes what its middle column asks for, and a
        `tablist` without `tabpanel`s promises a screen reader a structure that
        is not on the page. `aria-pressed` says exactly what is true.
      */}
      <div className="split-editor__modes">
        {SPLIT_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            aria-pressed={split.mode === mode}
            className={`tab${split.mode === mode ? ' tab--on' : ''}`}
            onClick={() => onChange(withMode(split, mode as SplitMode, amountCents, memberOrder))}
          >
            {MODE_LABELS[mode]}
          </button>
        ))}
      </div>

      {/*
        The hint and the rows are keyed by the mode, so switching tabs
        cross-fades what they say instead of cutting to it. Opacity only, and
        the list is the same height and the same shape either way — nothing a
        finger is already over moves out from under it.
      */}
      <p className="muted note fade-swap" key={`hint-${split.mode}`}>
        {MODE_HINTS[split.mode]}
      </p>

      <ul className="split-editor__rows fade-swap" key={`rows-${split.mode}`}>
        {members.map((member) => {
          const included = split.included.includes(member.userId)
          const share = owed.get(member.userId)
          const name = member.userId === myId ? `${member.displayName} (you)` : member.displayName

          return (
            <li key={member.userId} className={`split-row${included ? '' : ' split-row--out'}`}>
              <label className="split-row__who">
                <input
                  type="checkbox"
                  checked={included}
                  onChange={() => onChange(toggleParticipant(split, member.userId))}
                />
                {name}
                {member.leftAt !== null && <span className="pill">left</span>}
              </label>

              {field !== null && (
                <span className="split-row__input">
                  {field.prefix && (
                    <span aria-hidden="true" className="split-row__fix">
                      {field.prefix}
                    </span>
                  )}
                  <input
                    inputMode={split.mode === 'shares' ? 'numeric' : 'decimal'}
                    disabled={!included}
                    aria-label={`${name}, ${field.hint}`}
                    value={split[field.key][member.userId] ?? ''}
                    onChange={(event) =>
                      onChange(withTyped(split, field.key, member.userId, event.target.value))
                    }
                  />
                  {field.suffix && (
                    <span aria-hidden="true" className="split-row__fix">
                      {field.suffix}
                    </span>
                  )}
                </span>
              )}

              <span className="split-row__owes">
                {included && share !== undefined ? formatMoney(share, currency) : '—'}
              </span>
            </li>
          )
        })}
      </ul>

      <p className={`split-editor__sum ${preview.error === null ? 'ok' : 'bad'}`} role="status">
        {preview.error === null
          ? `Adds up to ${formatMoney(preview.totalCents, currency)}.`
          : preview.error}
      </p>
    </fieldset>
  )
}
